import React, { useState } from 'react';
import { getFirestore, collection, addDoc } from 'firebase/firestore';
import './MembershipPage.css';

const MembershipForm = () => {
  const [formData, setFormData] = useState({
    schoolName: '',
    contactName: '',
    position: '',
    email: '',
    phone: '',
    numStudents: '',
    hearAboutUs: '',
    otherSource: '',
    additionalInfo: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.schoolName.trim()) newErrors.schoolName = 'School name is required';
    if (!formData.contactName.trim()) newErrors.contactName = 'Contact name is required';
    if (!formData.position.trim()) newErrors.position = 'Position is required';
    
    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    // Phone validation (now required)
    if (!formData.phone) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+?[\d\s()-]{10,15}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    // Number of students validation
    if (!formData.numStudents) {
      newErrors.numStudents = 'Number of students is required';
    } else if (isNaN(formData.numStudents) || parseInt(formData.numStudents) <= 0) {
      newErrors.numStudents = 'Please enter a valid number';
    }
    
    // How did you hear about us validation
    if (!formData.hearAboutUs) {
      newErrors.hearAboutUs = 'This field is required';
    }
    
    // Validate 'other' field if 'other' is selected
    if (formData.hearAboutUs === 'other' && !formData.otherSource.trim()) {
      newErrors.otherSource = 'Please specify how you heard about us';
    }
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = validateForm();
    
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Save form data to Firestore
      const db = getFirestore();
      const schoolRef = collection(db, 'schoolMemberships');
      
      // Add timestamp to the form data
      const schoolData = {
        ...formData,
        createdAt: new Date(),
        paymentStatus: 'pending',
      };
      
      // Add document to Firestore and get the document ID
      const docRef = await addDoc(schoolRef, schoolData);
      console.log('School membership form saved with ID:', docRef.id);
      
      // Store the document ID in localStorage
      localStorage.setItem('pendingSchoolMembershipId', docRef.id);
      
      // Redirect to Stripe payment page
      window.location.href = 'https://buy.stripe.com/28oaET0k82hugbS3cc';
      
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrors({ submit: 'There was an error submitting the form. Please try again.' });
      setIsSubmitting(false);
    }
  };

  return (
    <div className="membership-form-container">
      <form onSubmit={handleSubmit} className="membership-form">
        <div className="form-section">
          <h3>School Information</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="schoolName">School Name*</label>
              <input
                type="text"
                id="schoolName"
                name="schoolName"
                value={formData.schoolName}
                onChange={handleChange}
                className={errors.schoolName ? 'error' : ''}
              />
              {errors.schoolName && <span className="error-message">{errors.schoolName}</span>}
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="numStudents">Number of Students*</label>
            <input
              type="number"
              id="numStudents"
              name="numStudents"
              value={formData.numStudents}
              onChange={handleChange}
              min="1"
              className={errors.numStudents ? 'error' : ''}
            />
            {errors.numStudents && <span className="error-message">{errors.numStudents}</span>}
          </div>
        </div>
        
        <div className="form-section">
          <h3>Contact Information</h3>
          <div className="form-row two-columns">
            <div className="form-group">
              <label htmlFor="contactName">Contact Name*</label>
              <input
                type="text"
                id="contactName"
                name="contactName"
                value={formData.contactName}
                onChange={handleChange}
                className={errors.contactName ? 'error' : ''}
              />
              {errors.contactName && <span className="error-message">{errors.contactName}</span>}
            </div>
            
            <div className="form-group">
              <label htmlFor="position">Position*</label>
              <input
                type="text"
                id="position"
                name="position"
                value={formData.position}
                onChange={handleChange}
                placeholder="e.g., Principal, Administrator"
                className={errors.position ? 'error' : ''}
              />
              {errors.position && <span className="error-message">{errors.position}</span>}
            </div>
          </div>
          
          <div className="form-row two-columns">
            <div className="form-group">
              <label htmlFor="email">Email*</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={errors.email ? 'error' : ''}
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>
            
            <div className="form-group">
              <label htmlFor="phone">Phone Number*</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="e.g., +1 (123) 456-7890"
                className={errors.phone ? 'error' : ''}
              />
              {errors.phone && <span className="error-message">{errors.phone}</span>}
            </div>
          </div>
        </div>
        
        <div className="form-section">
          <h3>Additional Information</h3>
          <div className="form-group">
            <label htmlFor="hearAboutUs">How did you hear about us?*</label>
            <select
              id="hearAboutUs"
              name="hearAboutUs"
              value={formData.hearAboutUs}
              onChange={handleChange}
              className={errors.hearAboutUs ? 'error' : ''}
            >
              <option value="">Select an option</option>
              <option value="conference">Conference</option>
              <option value="friend">Friend or colleague</option>
              <option value="social">Social media</option>
              <option value="other">Other</option>
            </select>
            {errors.hearAboutUs && <span className="error-message">{errors.hearAboutUs}</span>}
          </div>
          
          {formData.hearAboutUs === 'other' && (
            <div className="form-group">
              <label htmlFor="otherSource">Please specify*</label>
              <input
                type="text"
                id="otherSource"
                name="otherSource"
                value={formData.otherSource}
                onChange={handleChange}
                className={errors.otherSource ? 'error' : ''}
              />
              {errors.otherSource && <span className="error-message">{errors.otherSource}</span>}
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="additionalInfo">Any specific needs or questions?</label>
            <textarea
              id="additionalInfo"
              name="additionalInfo"
              rows="4"
              value={formData.additionalInfo}
              onChange={handleChange}
              placeholder="Please share any specific requirements or questions you have"
            ></textarea>
          </div>
        </div>
        
        {errors.submit && <div className="error-message submit-error">{errors.submit}</div>}
        
        <div className="form-actions">
          <button 
            type="submit" 
            className="submit-button" 
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Application'}
          </button>
          <p className="form-disclaimer">
            By submitting this form, you agree to our <a href="/terms-of-service">Terms of Service</a> and <a href="/privacy-policy">Privacy Policy</a>.
          </p>
        </div>
      </form>
    </div>
  );
};

export default MembershipForm;